import React from 'react';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { getImageUrl } from '../lib/imageUtils';

const Footer = ({ settings = {}, contactInfo = {}, footer = {} }) => {
  const companyName = settings.company_name || 'Narain Trading Co.';
  const logoUrl = getImageUrl(settings.logo_url);

  // Footer-specific content (with safe fallbacks)
  const tagline = footer.tagline || 'Since Years';
  const description = footer.description || 'Your trusted partner for premium quality pipes and valves.';
  const googleReviewsText = footer.google_reviews_text || '4.6/5 Google Reviews';
  const quickLinksTitle = footer.quick_links_title || 'Quick Links';
  const productsTitle = footer.products_title || 'Our Products';
  const contactTitle = footer.contact_title || 'Contact Info';
  const copyright = footer.copyright || '© 2024 Narain Trading Co. All rights reserved.';
  const partnerLabel = footer.partner_label || 'Authorized Distributor of';

  // Build quick links from Footer tab
  const quickLinks = [
    { label: footer.link_1_label || 'About Us', target: footer.link_1_target || 'about' },
    { label: footer.link_2_label || 'Products', target: footer.link_2_target || 'products' },
    { label: footer.link_3_label || 'Services', target: footer.link_3_target || 'services' },
    { label: footer.link_4_label || 'Contact', target: footer.link_4_target || 'contact' },
  ].filter(l => l.label && l.target);

  // Build product list from Footer tab
  const productList = [
    footer.product_1, footer.product_2, footer.product_3,
    footer.product_4, footer.product_5, footer.product_6
  ].filter(Boolean);

  // Partner brands
  const partnerBrands = [footer.partner_brand_1, footer.partner_brand_2].filter(Boolean);

  // Contact info
  const phone = contactInfo.phone || '0522 410 2175';
  const whatsapp = contactInfo.whatsapp || '+91-9118946361';
  const whatsappNumber = contactInfo.whatsapp_number || '919118946361';
  const email = contactInfo.email || 'info@naraintrading.com';
  const addressLine1 = contactInfo.address_line1 || 'B-93, Vibhuti Khand, Gomti Nagar,';
  const addressLine2 = contactInfo.address_line2 || 'Lucknow, Uttar Pradesh 226010';
  const hoursDays = contactInfo.business_hours_days || 'Monday - Sunday';
  const hoursTime = contactInfo.business_hours_time || '11:00 AM - 8:00 PM';

  return (
    <footer className="bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 text-white pt-16 pb-8">
      <div className="container mx-auto px-4">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* Company Info */}
          <div>
            <div className="flex items-center space-x-3 mb-4">
              {logoUrl ? (
                <img src={logoUrl} alt={companyName} className="w-12 h-12 object-contain rounded-lg bg-white p-1" />
              ) : (
                <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold">NT</span>
                </div>
              )}
              <div>
                <h3
                  className="text-2xl font-black tracking-tight leading-none"
                  style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                >
                  <span className="text-white">Narain</span>
                  <span className="text-yellow-400 italic"> Trading</span>
                  <span className="text-white"> Co.</span>
                </h3>
                <p className="text-[10px] uppercase tracking-[0.2em] text-yellow-500/80 font-semibold mt-1">{tagline}</p>
              </div>
            </div>
            <p className="text-gray-300 text-sm leading-relaxed mb-4">
              {description}
            </p>
            <div className="flex items-center space-x-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <svg key={star} className="w-4 h-4 text-yellow-400 fill-current" viewBox="0 0 20 20">
                  <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                </svg>
              ))}
              <span className="text-sm text-gray-300 ml-2">{googleReviewsText}</span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-bold mb-4">{quickLinksTitle}</h4>
            <ul className="space-y-2">
              {quickLinks.map((link, idx) => (
                <li key={idx}>
                  <button
                    onClick={() => document.getElementById(link.target)?.scrollIntoView({ behavior: 'smooth' })}
                    className="text-gray-300 hover:text-yellow-400 transition-colors text-sm text-left"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Our Products */}
          <div>
            <h4 className="text-lg font-bold mb-4">{productsTitle}</h4>
            <ul className="space-y-2 text-sm text-gray-300">
              {productList.map((p, idx) => (
                <li key={idx}>{p}</li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-bold mb-4">{contactTitle}</h4>
            <div className="space-y-3 text-sm">
              <div className="flex items-start space-x-2">
                <MapPin size={16} className="text-yellow-400 flex-shrink-0 mt-1" />
                <span className="text-gray-300">
                  {addressLine1} {addressLine2}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Phone size={16} className="text-yellow-400 flex-shrink-0" />
                <a href={`tel:${phone.replace(/\s/g, '')}`} className="text-gray-300 hover:text-yellow-400 transition-colors">
                  {phone}
                </a>
              </div>
              <div className="flex items-center space-x-2">
                <Phone size={16} className="text-green-400 flex-shrink-0" />
                <a href={`https://wa.me/${whatsappNumber}`} className="text-gray-300 hover:text-green-400 transition-colors">
                  {whatsapp}
                </a>
              </div>
              <div className="flex items-center space-x-2">
                <Mail size={16} className="text-yellow-400 flex-shrink-0" />
                <a href={`mailto:${email}`} className="text-gray-300 hover:text-yellow-400 transition-colors break-all">
                  {email}
                </a>
              </div>
              <div className="flex items-start space-x-2">
                <Clock size={16} className="text-yellow-400 flex-shrink-0 mt-1" />
                <span className="text-gray-300">{hoursDays}: {hoursTime}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-700 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-400 text-sm">
              {copyright}
            </p>
            {partnerBrands.length > 0 && (
              <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-gray-400">
                <span>{partnerLabel}</span>
                {partnerBrands.map((brand, idx) => (
                  <React.Fragment key={idx}>
                    <span className="font-semibold text-white">{brand}</span>
                    {idx < partnerBrands.length - 1 && <span className="hidden md:inline">|</span>}
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
